import type { NextPage } from 'next'
import { RecurlyAccount, CouponState, isError } from '../../types/types'
import {
  getRecurlyAccount,
  getActivationStatus,
} from '../../services/SubscriptionApiClient'
import { useEffect, useState, useContext } from 'react'
import * as Sentry from '@sentry/nextjs'
import SubcriptionOverview from '../../components/Subscription/SubcriptionOverviewComponent'
import SubscriptionPlanComponent from '../../components/Subscription/SubscriptionPlanComponent'
import ListCouponsComponent from '../../components/Coupons/ListCouponsComponent'
import PaymentDetailsComponent from '../../components/Subscription/PaymentDetailsComponent'
import Script from 'next/script'
import { useRouter } from 'next/router'
import Spinner from '../../components/Spinner'
import { CouponContext, SubscriptionContext } from '../../components/context'
import DownloadCompanionModal from '../../components/DownloadCompanionModal'
import { NondescriptError } from '../../components/NondescriptError'
import { Elements, RecurlyProvider } from '@recurly/react-recurly'

const Subscription: NextPage = () => {
  const { coupons } = useContext(CouponContext)
  const {
    subscription,
    loading: subscriptionLoading,
    updateSubscription,
  } = useContext(SubscriptionContext)
  const [account, setAccount] = useState<RecurlyAccount | Error>()
  const [accountLoading, setAccountLoading] = useState(false)
  const activeCoupons = coupons.filter(
    (coupon) => coupon.state === CouponState.ACTIVE
  )
  const router = useRouter()
  const [showDownloadCompanionModal, setShowDownloadCompanionModal] =
    useState(false)

  useEffect(() => {
    async function getData() {
      setAccountLoading(true)
      try {
        setAccount(await getRecurlyAccount())
      } catch (e) {
        setAccount(e as Error)
      }
      setAccountLoading(false)
      try {
        setShowDownloadCompanionModal(
          !(await getActivationStatus()).openedCompanion
        )
      } catch (e) {
        Sentry.captureException(e)
      }
    }
    getData()
  }, [])

  if (!subscriptionLoading && !subscription) {
    router.push('/signup')
  }

  if (subscriptionLoading || accountLoading) {
    return (
      <>
        <Spinner />
      </>
    )
  }

  if (isError(subscription) || isError(account)) {
    return (
      <NondescriptError message="Failed to fetch information related to payments and subscriptions." />
    )
  }

  return (
    <>
      <Script
        src="https://js.recurly.com/v4/recurly.js"
        strategy="beforeInteractive"
      ></Script>

      {subscription && account ? (
        <>
          <DownloadCompanionModal
            show={showDownloadCompanionModal}
            close={() => setShowDownloadCompanionModal(false)}
          />
          <div className="mb-60">
            <h2 className="h4">Reason+ subscription</h2>
            <hr />
            <SubcriptionOverview
              subscription={subscription}
              account={account}
            />
          </div>
          <RecurlyProvider
            publicKey={process.env.NEXT_PUBLIC_RECURLY_PUBLIC_KEY || ''}
          >
            <Elements>
              {account && (
                <div className="mb-60">
                  <h4>Payment Details</h4>
                  <PaymentDetailsComponent
                    account={account}
                    setAccount={setAccount}
                  />
                </div>
              )}
              <div className="mb-60">
                <h4>Reason+ Plan</h4>
                <SubscriptionPlanComponent
                  currentSubscription={subscription}
                  setCurrentSubscription={updateSubscription}
                />
              </div>

              {activeCoupons.length !== 0 && (
                <div className="mb-60">
                  <h4>Active coupons</h4>
                  <ListCouponsComponent
                    coupons={activeCoupons}
                    loaded={coupons.length !== 0}
                  />
                </div>
              )}
            </Elements>
          </RecurlyProvider>
        </>
      ) : (
        <Spinner />
      )}
    </>
  )
}

export default Subscription
